import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram, Sparkles, Star } from 'lucide-react';
import { CRUNCH_FLAVORS, PILLARS, TESTIMONIALS, BARK_FLAVORS, FOUNDER } from '../mock';
import InstagramFeedEmbed from '../components/InstagramFeedEmbed';

// Placeholder tile for flavors without HD image yet
const FlavorVisual = ({ f, className = '' }) => (
  f.image ? (
    <img src={f.image} alt={f.name} className={className} style={{ filter: 'drop-shadow(0 40px 40px rgba(26, 18, 11, 0.35))' }} />
  ) : (
    <div className={`${className} flex items-center justify-center rounded-full`} style={{ background: `linear-gradient(160deg, ${f.color} 0%, #1a0d08 100%)` }}>
      <div className="flex flex-col items-center justify-center text-[#f6efe1]">
        <span className="font-display italic text-2xl opacity-70">Raretto</span>
        <span className="tracking-luxe text-[10px] mt-2 opacity-90">CRUNCH</span>
        <span className="font-display text-3xl mt-1">{f.name}</span>
      </div>
    </div>
  )
);

const Home = () => {
  const [heroIdx, setHeroIdx] = useState(0);
  const hero = CRUNCH_FLAVORS[heroIdx];

  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % CRUNCH_FLAVORS.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <main>
      {/* HERO — brand-first */}
      <section className="relative overflow-hidden bg-[#f6efe1] grain">
        <div className="container-lux relative z-10 grid md:grid-cols-2 gap-12 md:items-start pt-16 pb-20 md:pt-28 md:pb-32">
          <div className="space-y-8 fade-up pb-2">
            <p className="tracking-luxe text-[#b78d3c]">The Rare House · Mumbai</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[86px] leading-[1.02] text-[#1a120b]">
              A chocolate<br />
              <span className="italic font-serif-alt">worth pausing</span> <br />
              for.
            </h1>
            <p className="text-lg text-[#3a2a1e]/80 max-w-md leading-relaxed">
              Raretto is a modern chocolate house, built on many generations of craftsmanship and one quiet obsession, that India’s flavour deserved chocolate composed to the standard of the world’s finest.
            </p>
            <div className="flex flex-wrap items-center gap-4 pb-2 md:pb-0">
              <Link to="/build-a-box" className="btn-primary">Curate a Box <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/about" className="btn-ghost">The House</Link>
            </div>
          </div>

          {/* Rotating flavour — all 13 */}
          <div className="relative aspect-square max-w-lg mx-auto w-full">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#efe6d3] to-[#e0d4b6]"></div>
            <div className="absolute inset-4 rounded-full border border-[#b78d3c]/30"></div>
            <div className="absolute inset-8 rounded-full border border-[#b78d3c]/15"></div>
            {CRUNCH_FLAVORS.map((f, i) => (
              <div key={f.id} className={`absolute inset-0 rounded-full overflow-hidden transition-all duration-1000 ${i === heroIdx ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                <FlavorVisual f={f} className="w-full h-full object-cover" />
              </div>
            ))}
            <div className="absolute -top-2 -right-2 bg-[#1a120b] text-[#f6efe1] px-4 py-3 rotate-6">
              <p className="tracking-luxe text-[#b78d3c] text-[10px]">Now Showing</p>
              <p className="font-display text-lg">{hero.name}</p>
            </div>
            {/* Ring counter of all 13 */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex flex-wrap gap-1.5 max-w-xs justify-center">
              {CRUNCH_FLAVORS.map((f, i) => (
                <button key={f.id} onClick={() => setHeroIdx(i)} className={`h-1 transition-all ${i === heroIdx ? 'w-8 bg-[#1a120b]' : 'w-3 bg-[#1a120b]/25 hover:bg-[#1a120b]/50'}`} aria-label={f.name} />
              ))}
            </div>
          </div>
        </div>

        {/* Trust strip — quiet */}
        <div className="border-y border-[#1a120b]/12 bg-[#efe6d3]">
          <div className="container-lux grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a120b]/10">
            {[
              { t: 'Belgian couverture' },
              // { t: 'Mumbai atelier' },
              { t: 'Small-batch, hand-finished' },
              { t: 'Thirteen signatures' },
            ].map((x, i) => (
              <div key={i} className="px-6 py-6 text-center">
                <p className="font-display text-lg text-[#1a120b]">{x.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILD A BOX — the ritual */}
      <section className="section bg-[#efe6d3]">
        <div className="container-lux grid md:grid-cols-2 gap-16 items-center">
          <div className="bg-gradient-to-br from-[#e5d6b3] to-[#c9b58a] relative p-8 pb-28">
            <div className="grid grid-cols-4 gap-3">
              {CRUNCH_FLAVORS.map((f) => (

                <div key={f.id} className={`bg-white/40 backdrop-blur rounded-full aspect-square overflow-hidden flex items-center justify-center transition-transform duration-500 hover:scale-110 hover:-rotate-6 ${f.id === 'caramel' ? 'col-start-2 translate-x-1/2' : ''}`}>
                  <FlavorVisual f={f} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <div className="absolute bottom-6 left-6 right-6 bg-[#1a120b] text-[#f6efe1] p-5">
              <p className="tracking-luxe text-[#b78d3c] text-[10px]">Composed by you</p>
              <p className="mt-1 text-sm">Thirteen flavours. Any combination. Priced by the piece.</p>
            </div>
          </div>
          <div className="space-y-6">
            <p className="tracking-luxe text-[#b78d3c]">The Ritual</p>
            <h2 className="font-display text-5xl md:text-6xl leading-tight">Curate a private box.</h2>
            <p className="text-[#3a2a1e]/80 text-lg leading-relaxed max-w-lg">
              Choose a size. Compose your selection, piece by piece. Send eight or send twenty-four. Discreet packaging. Handwritten cards on request.
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[8, 12, 24].map((n) => (
                <div key={n} className="aspect-square border border-[#1a120b] flex flex-col items-center justify-center hover:bg-[#1a120b] hover:text-[#f6efe1] transition-colors cursor-pointer">
                  <p className="font-display text-3xl">{n}</p>
                  <p className="tracking-luxe mt-1 text-[10px]">pieces</p>
                </div>
              ))}
            </div>
            <div className="pt-4">
              <Link to="/build-a-box" className="btn-primary">Begin <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND STORY — evolution */}
      <section className="section">
        <div className="container-lux max-w-4xl">
          <p className="tracking-luxe text-[#b78d3c] mb-6">On Inheritance</p>
          <div className="font-display text-4xl md:text-6xl leading-[1.1] text-[#1a120b] space-y-2">
            <p>Some families inherit recipes.</p>
            <p className="text-[#1a120b]/55">Others inherit businesses.</p>
            <p className="italic font-serif-alt">We inherited an obsession with craftsmanship.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-10 mt-16 pt-10 border-t border-[#1a120b]/15">
            {[
              'Many generations understood ingredients.',
              'Many generations understood flavour.',
              'This one asked a different question.',
            ].map((line, i) => (
              <p key={i} className="font-serif-alt text-2xl text-[#1a120b]/85 leading-snug">{line}</p>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/about" className="nav-link tracking-luxe text-[#1a120b] hover:text-[#b78d3c]">Read the story →</Link>
          </div>
        </div>
      </section>

      {/* PILLARS — elevated feature block */}
      <section className="section bg-[#1a120b] text-[#f6efe1] relative overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#b78d3c]/10 rounded-full blur-3xl"></div>
        <div className="container-lux relative z-10">
          <div className="max-w-2xl mb-16">
            <p className="tracking-luxe text-[#b78d3c] mb-3">The House</p>
            <h2 className="font-display text-5xl md:text-6xl">Composed. Not made.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {PILLARS.map((f, i) => (
              <div key={i} className="group border-t border-[#f6efe1]/20 pt-6">
                <p className="tracking-luxe text-[#b78d3c] mb-4">0{i + 1}</p>
                <h3 className="font-display text-2xl mb-4">{f.title}</h3>
                <p className="text-[#f6efe1]/70 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE CRUNCH — taste, don't sell */}
      {/* <section className="section">
        <div className="container-lux grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <p className="tracking-luxe text-[#b78d3c]">The Crunch</p>
            <h2 className="font-display text-5xl md:text-6xl leading-tight">Thirteen ways<br /><em className="font-serif-alt not-italic">to end an evening.</em></h2>
            <p className="text-[#3a2a1e]/80 text-lg leading-relaxed max-w-lg">
              Paan. Gulkand. Coffee from Chikmagalur. Alphonso from Ratnagiri. India’s vocabulary, translated into chocolate, the way it always should have been.
            </p>
            <div className="pt-4 flex gap-3">
              <Link to="/crunch" className="btn-primary">Read the Crunch story <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="relative">
            <div className="grid grid-cols-3 gap-3">
              {CRUNCH_FLAVORS.slice(0, 9).map((f) => (
                <div key={f.id} className="aspect-square rounded-full bg-[#efe6d3] halo overflow-hidden group">
                  <FlavorVisual f={f} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              ))}
            </div>
            <p className="tracking-luxe text-[#1a120b]/50 mt-4 text-right">+ 4 more signatures</p>
          </div>
        </div>
      </section> */}



      {/* FOUNDER — restrained */}
      <section className="section">
        <div className="container-lux grid md:grid-cols-[400px_1fr] gap-16 items-center">
          <div className="aspect-[4/5] bg-[#1a120b] relative overflow-hidden">
            {FOUNDER.image ? (
              <img src={FOUNDER.image} alt={FOUNDER.name} className="w-full h-full object-cover object-[center_15%]" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#f6efe1]">
                <div className="w-32 h-32 rounded-full bg-[#f6efe1]/8 border border-[#b78d3c]/40 flex items-center justify-center mb-6">
                  <span className="font-display text-5xl text-[#b78d3c]">SR</span>
                </div>
                <p className="tracking-luxe text-[#b78d3c] text-[10px]">Portrait</p>
                <p className="font-display text-2xl mt-2">{FOUNDER.name}</p>
                <p className="text-xs text-[#f6efe1]/50 mt-1">Placeholder · image pending</p>
              </div>
            )}
            <div className="absolute bottom-6 left-6 right-6 grain"></div>
          </div>
          <div>
            <p className="tracking-luxe text-[#b78d3c] mb-6">The Founder</p>
            <p className="font-serif-alt text-3xl md:text-4xl leading-snug text-[#1a120b] mb-8">
              “{FOUNDER.quote}”
            </p>
            <div className="gold-rule mb-6 max-w-xs"></div>
            <p className="font-display text-xl text-[#1a120b]">{FOUNDER.name}</p>
            <p className="tracking-luxe text-[#1a120b]/55 mt-1">{FOUNDER.title} · {FOUNDER.city}</p>
            <Link to="/about" className="inline-block mt-8 nav-link tracking-luxe text-[#1a120b] hover:text-[#b78d3c]">The full story →</Link>
          </div>
        </div>
      </section>

      {/* BARKS TEASER */}
      <section className="section bg-[#1a120b] text-[#f6efe1] relative overflow-hidden">
        <div className="container-lux relative z-10 grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 border border-[#b78d3c]/40 rounded-full px-4 py-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#b78d3c]" />
              <span className="tracking-luxe text-[#b78d3c] text-[10px]">Monsoon 2026</span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl leading-tight">A second form.<br /><em className="font-serif-alt not-italic text-[#b78d3c]">Barks.</em></h2>
            <p className="text-[#f6efe1]/75 text-lg max-w-lg leading-relaxed">
              Hand-broken slabs of premium chocolate, composed with fruit, nut and botanical inclusions. Six flavours, opening with Mango Chill.
            </p>
            <div className="pt-2">
              <Link to="/barks" className="btn-gold">The Barks Journal <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="relative aspect-square">
            <div className="grid grid-cols-2 gap-4 h-full">
              {BARK_FLAVORS.slice(0, 4).map((b, i) => (
                <div key={b.id} className={`relative flex items-end p-5 overflow-hidden ${i === 0 ? 'row-span-2' : ''}`} style={{ background: `linear-gradient(140deg, ${b.color}, #1a120b)` }}>
                  <div>
                    <p className="tracking-luxe text-[10px] opacity-80 mb-1">{b.badge}</p>
                    <p className="font-display text-xl leading-tight">{b.name}</p>
                    <p className="text-xs opacity-70 mt-1">{b.notes}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#b78d3c]/10 rounded-full blur-3xl"></div>
      </section>

      {/* IG REELS */}
      <section className="section">
        <div className="container-lux">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
            <div>
              <p className="tracking-luxe text-[#b78d3c] mb-3">The Journal</p>
              <h2 className="font-display text-5xl md:text-6xl">On Instagram</h2>
              <p className="text-[#3a2a1e]/70 mt-3">@raretto_chocolates</p>
            </div>
            <a href="https://www.instagram.com/raretto_chocolates/" target="_blank" rel="noreferrer" className="btn-ghost self-start md:self-end">
              <Instagram className="w-4 h-4" /> Follow
            </a>
          </div>

          <InstagramFeedEmbed />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section bg-[#efe6d3]">
        <div className="container-lux">
          <div className="text-center mb-14">
            <p className="tracking-luxe text-[#b78d3c] mb-3">Received</p>
            <h2 className="font-display text-5xl md:text-6xl">In their words.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 6).map((t, i) => (
              <figure key={i} className="bg-[#f6efe1] p-8 border border-[#1a120b]/8 relative">
                <div className="flex mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-[#b78d3c] text-[#b78d3c]" />)}</div>
                <blockquote className="font-serif-alt text-lg leading-relaxed text-[#1a120b] mb-8">“{t.text}”</blockquote>
                <figcaption className="flex items-center gap-3">
                  <img src={t.image} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <p className="font-medium">{t.name}</p>
                    <p className="text-xs text-[#1a120b]/55 tracking-widest uppercase">Verified</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
